import { DOCUMENT } from "@angular/common";
import { Component, Inject, OnInit, Renderer2, RendererFactory2 } from "@angular/core";
import { ActivatedRoute, NavigationEnd, Router } from "@angular/router";
import { EnvironmentConfig, LogService, SeoRouteData, SeoService } from "@rubbl-core";
import { GoogleTagManagerService } from "angular-google-tag-manager";
import { ToastrService } from "ngx-toastr";
import { filter, map, mergeMap } from "rxjs/operators";

declare let environment: EnvironmentConfig;

@Component({
  selector: "app-root",
  templateUrl: "./app.component.html",
})
export class AppComponent implements OnInit {
  toastrQueryStrings = ["Reset link sent."];
  t: boolean;

  constructor(
    private logService: LogService,
    private toastrService: ToastrService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private seoService: SeoService,
    private gtmService: GoogleTagManagerService,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private document: Document,
    rendererFactory: RendererFactory2,
  ) {
    this.logService.info(this.constructor.name, "Starting app");
    this.renderer = rendererFactory.createRenderer(null, null);
  }

  ngOnInit(): void {
    setTimeout(() => {
      // add gtm after we load
      this.gtmService.addGtmToDom();
    }, 2000);

    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        map(() => this.activatedRoute),
        map(route => {
          while (route.firstChild) route = route.firstChild;
          return route;
        }),
        filter(route => route.outlet === "primary"),
        mergeMap(route => route.data),
      )
      .subscribe(data => {
        this.seoService.setSeo(data as SeoRouteData);
        this.setCanonicalURLIfTrackingParams();
      });

    document.addEventListener("click", e => {
      const target = e.target as HTMLElement;
      const href = target.getAttribute("href");
      if (target.tagName === "A" && (!href || href == "#")) {
        e.preventDefault();
      }
    });

    this.checkForToastr();
  }

  setCanonicalURLIfTrackingParams() {
    const location = window.location;
    // Exclude canonical update if path contains "/equipment-rentals/"
    if (location.pathname.includes("/equipment-rentals/")) {
      return;
    }

    const searchParams = new URLSearchParams(location.search);

    // List of tracking param patterns (supports wildcards)
    const trackingParamPatterns = [
      /^fbclid$/i,
      /^gclid$/i,
      /^gclsrc$/i,
      /^utm_.*$/i,
      /^_ga$/i,
      /^mc_cid$/i,
      /^mc_eid$/i,
      /^_bta_tid$/i,
      /^_bta_c$/i,
      /^trk_.*$/i,
      /^gdfms$/i,
      /^gdftrk$/i,
      /^gdffi$/i,
      /^_ke$/i,
      /^sb_referer_host$/i,
      /^mkwid$/i,
      /^pcrid$/i,
      /^ef_id$/i,
      /^s_kwcid$/i,
      /^msclkid$/i,
      /^dm_i$/i,
      /^epik$/i,
      /^pk_.*$/i,
      /^piwik_.*$/i,
      /^mtm_.*$/i,
      /^matomo_.*$/i,
      /^hsa_.*$/i,
      /^_branch_match_id$/i,
      /^mkevt$/i,
      /^mkcid$/i,
      /^mkrid$/i,
      /^campid$/i,
      /^toolid$/i,
      /^customid$/i,
      /^igshid$/i,
      /^si$/i,
      /^sms_.*$/i,
    ];

    // Remove tracking params from current query string
    const canonicalParams = new URLSearchParams();
    searchParams.forEach((value, key) => {
      const isTracking = trackingParamPatterns.some(pattern => pattern.test(key)) || key.includes("_");
      if (!isTracking) {
        canonicalParams.append(key, value);
      }
    });

    // Build canonical URL with only non-tracking params (if any)
    let canonicalUrl = location.origin + location.pathname;
    const query = canonicalParams.toString();
    if (query) canonicalUrl += `?${query}`;
    if (location.hash) canonicalUrl += location.hash; // preserve hash/fragment

    let link: HTMLLinkElement = this.document.querySelector("link[rel='canonical']");
    if (link) {
      this.renderer.setAttribute(link, "href", canonicalUrl);
    } else {
      link = this.renderer.createElement("link");
      this.renderer.setAttribute(link, "rel", "canonical");
      this.renderer.setAttribute(link, "href", canonicalUrl);
      this.renderer.appendChild(this.document.head, link);
    }
  }

  checkForToastr(): void {
    // check if there is an toastr message to show
    const urlParams = new URLSearchParams(window.location.search);
    if (this.toastrQueryStrings.includes(urlParams.get("toastrMessage"))) {
      setTimeout(
        () =>
          this.toastrService.success(urlParams.get("toastrMessage"), null, {
            positionClass: "toast-top-center",
          }),
        800,
      );
      window.history.replaceState({}, window.document.title, window.location.origin);
    }
  }
}
